// ============================================================
// AURAS (v8.368.8) - an exposure with a radius, read by proximity.
//
// A code with `radius=N` is not something the bearer wears against
// whoever attacks them: it radiates. Whoever stands within N metres and
// inside its scope READS it, and it then behaves as if their own target
// wore it - a Rallying banner is +10 to hit anyone, an Awe aura makes
// every attack a hostile nearby dares a Willpower test. The gathering
// is done once per reader, from token distances, and handed to every
// reader seam as `ExposureReader.auras` (the roll, the ledger and both
// attacker forecasts read the SAME list, so they agree by construction).
//
// The bearer stands at 0 m and is inside their own radius: a banner
// rallies its bearer too, when the scope lets it (`@ally` does, `@hostile`
// never). Pure. Zero React dependency.
// ============================================================
import { parseExposure, scopeApplies, axisTotal, type ExposureBearer } from './exposures';
import { isObject } from './bodyShape';

/** The two coordinates an aura is measured over: ground position, in metres. */
export interface AuraPoint {
  x: number;
  z: number;
}

/** Someone who may radiate: their id, what they wear, and their team. */
export interface AuraSource {
  id: string;
  bearer: ExposureBearer;
}

/** The bearer's aura codes that reach a reader `distanceM` away and inside their scope. */
export function aurasFrom(
  bearer: ExposureBearer,
  reader: { teamTag?: string | null; sideTag?: string | null },
  distanceM: number | null | undefined,
): string[] {
  if (distanceM === null || distanceM === undefined) return [];
  const out: string[] = [];
  for (const code of [...(bearer.exposures ?? []), ...(bearer.conditions ?? [])]) {
    const ex = parseExposure(code);
    if (ex?.radius && distanceM <= ex.radius && scopeApplies(ex, bearer, reader)) out.push(code);
  }
  return out;
}

/**
 * Every reader's auras at once: for each person with a token, the codes
 * radiated by everyone (themselves included) within reach. Somebody
 * without a token stands nowhere and reads nothing.
 */
export function aurasByProximity(
  people: readonly AuraSource[],
  tokens: Record<string, AuraPoint>,
): Map<string, string[]> {
  const out = new Map<string, string[]>();
  const radiating = people.filter((p) => tokens[p.id] && hasAura(p.bearer));
  if (!radiating.length) return out;
  // An Object reads no aura (2026-10-26): there is nothing in it to be steadied or shaken.
  for (const reader of people.filter((p) => !isObject(p.bearer as { bodyType?: string }))) {
    const at = tokens[reader.id];
    const codes = at ? aurasAt(reader, at, radiating, tokens) : [];
    if (codes.length) out.set(reader.id, codes);
  }
  return out;
}

/** The codes every radiating source lays on one reader standing at `at`. */
function aurasAt(
  reader: AuraSource,
  at: AuraPoint,
  radiating: readonly AuraSource[],
  tokens: Record<string, AuraPoint>,
): string[] {
  return radiating.flatMap((src) => {
    const from = tokens[src.id];
    return aurasFrom(src.bearer, reader.bearer, Math.hypot(at.x - from.x, at.z - from.z));
  });
}

function hasAura(bearer: ExposureBearer): boolean {
  return [...(bearer.exposures ?? []), ...(bearer.conditions ?? [])].some((c) => parseExposure(c)?.radius);
}

/** The `initiative` axis summed over the auras a reader stands under. */
export function auraInitiative(auras: readonly string[] | undefined): number {
  if (!auras?.length) return 0;
  const parsed = auras.flatMap((c) => {
    const ex = parseExposure(c);
    return ex ? [ex] : [];
  });
  return axisTotal(parsed, 'initiative');
}
